import { Button, Modal } from "antd";
import { Download, ExternalLink, FileText, X, ZoomIn, ZoomOut, RotateCw } from "lucide-react";
import React, { useState } from "react";

interface ViewAttachmentModalProps {
  showModal:     boolean;
  attachmentUrl: string;
  fileName?:     string;
  onCancel:      () => void;
}

const ViewAttachmentModal: React.FC<ViewAttachmentModalProps> = ({
  showModal,
  attachmentUrl,
  fileName,
  onCancel,
}) => {
  const [zoom,   setZoom]   = useState(1);
  const [rotate, setRotate] = useState(0);

  const isImage = /\.(jpg|jpeg|png|gif|webp)$/i.test(attachmentUrl);
  const isPdf   = /\.pdf$/i.test(attachmentUrl);

  function handleZoomIn()  { setZoom((prev) => Math.min(prev + 0.25, 3)); }
  function handleZoomOut() { setZoom((prev) => Math.max(prev - 0.25, 0.5)); }
  function handleRotate()  { setRotate((prev) => (prev + 90) % 360); }

  function handleClose() {
    // Reset image controls when closing
    setZoom(1);
    setRotate(0);
    onCancel();
  }

  // Derive a display name from the URL if no fileName provided
  const displayName = fileName ?? attachmentUrl.split("/").pop() ?? "attachment";

  return (
    <Modal
      open={showModal}
      onCancel={handleClose}
      footer={null}
      centered
      // Wider modal for PDFs; image modal adapts to content
      width={isPdf ? "90vw" : 700}
      style={{ maxWidth: "95vw" }}
      styles={{ body: { padding: 0 } }}
      closeIcon={
        <X size={18} className="text-gray-500 hover:text-gray-800 transition-colors" />
      }
      title={
        <div className="flex items-center gap-2 px-2 pt-1">
          {isImage ? (
            <span className="text-base font-semibold text-gray-700 truncate max-w-[400px]">
              {displayName}
            </span>
          ) : (
            <div className="flex items-center gap-2">
              <FileText size={18} className="text-blue-500 shrink-0" />
              <span className="text-base font-semibold text-gray-700 truncate max-w-[400px]">
                {displayName}
              </span>
            </div>
          )}
        </div>
      }
    >
      {/* ── Image Viewer ─────────────────────────────────────────────────────── */}
      {isImage && (
        <div className="flex flex-col">
          {/* Toolbar */}
          <div className="flex items-center justify-between gap-2 px-4 py-2 border-b bg-gray-50">
            <div className="flex items-center gap-1">
              <Button
                size="small"
                icon={<ZoomOut size={14} />}
                onClick={handleZoomOut}
                disabled={zoom <= 0.5}
                title="Zoom out"
              />
              <span className="text-xs text-gray-500 w-12 text-center">
                {Math.round(zoom * 100)}%
              </span>
              <Button
                size="small"
                icon={<ZoomIn size={14} />}
                onClick={handleZoomIn}
                disabled={zoom >= 3}
                title="Zoom in"
              />
              <Button
                size="small"
                icon={<RotateCw size={14} />}
                onClick={handleRotate}
                title="Rotate 90°"
                className="ml-1"
              />
            </div>
            <div className="flex items-center gap-1">
              <Button
                size="small"
                icon={<ExternalLink size={14} />}
                onClick={() => window.open(attachmentUrl, "_blank")}
                title="Open in new tab"
              >
                Open
              </Button>
              <Button
                size="small"
                icon={<Download size={14} />}
                href={attachmentUrl}
                download={displayName}
                title="Download"
              >
                Download
              </Button>
            </div>
          </div>

          {/* Image display */}
          <div
            className="overflow-auto flex items-center justify-center bg-gray-100"
            style={{ maxHeight: "70vh", minHeight: "300px" }}
          >
            <img
              src={attachmentUrl}
              alt={displayName}
              style={{
                transform:     `scale(${zoom}) rotate(${rotate}deg)`,
                transition:    "transform 0.2s ease",
                maxWidth:      "100%",
                maxHeight:     "65vh",
                objectFit:     "contain",
                cursor:        zoom < 3 ? "zoom-in" : "zoom-out",
              }}
              onClick={() => (zoom < 3 ? handleZoomIn() : handleZoomOut())}
            />
          </div>
        </div>
      )}

      {/* ── PDF Viewer ───────────────────────────────────────────────────────── */}
      {isPdf && (
        <div className="flex flex-col">
          {/* Toolbar */}
          <div className="flex items-center justify-end gap-1 px-4 py-2 border-b bg-gray-50">
            <Button
              size="small"
              icon={<ExternalLink size={14} />}
              onClick={() => window.open(attachmentUrl, "_blank")}
            >
              Open in new tab
            </Button>
            <Button
              size="small"
              icon={<Download size={14} />}
              href={attachmentUrl}
              download={displayName}
            >
              Download
            </Button>
          </div>
          {/* Embedded PDF */}
          <iframe
            src={attachmentUrl}
            title={displayName}
            style={{ width: "100%", height: "75vh", border: "none" }}
          />
        </div>
      )}

      {/* ── Unsupported file type ─────────────────────────────────────────────── */}
      {!isImage && !isPdf && (
        <div className="flex flex-col items-center justify-center gap-4 py-12 px-6 text-center">
          <FileText size={48} className="text-gray-300" />
          <p className="text-gray-500 text-sm">
            This file type cannot be previewed.
          </p>
          <div className="flex gap-2">
            <Button
              icon={<ExternalLink size={14} />}
              onClick={() => window.open(attachmentUrl, "_blank")}
            >
              Open in new tab
            </Button>
            <Button
              type="primary"
              icon={<Download size={14} />}
              href={attachmentUrl}
              download={displayName}
            >
              Download
            </Button>
          </div>
        </div>
      )}
    </Modal>
  );
};

export default ViewAttachmentModal;